import { useContext } from 'react';
import PropTypes from 'prop-types';
import i18nContext from 'components/i18n-context';
import { formatDate } from 'services/utils';
import { ReactComponent as UploadIcon } from 'assets/upload-requested-documents-icon.svg';
import { ReactComponent as CalendarIcon } from 'assets/calendar-icon.svg';
import { ReactComponent as ProfileIcon } from 'assets/iconButtons/profile.svg';
import { ReactComponent as WalletIcon } from 'assets/wallet.svg';
import { ReactComponent as TransactionsIcon } from 'assets/iconButtons/transactions.svg';
import { ReactComponent as ActivityIcon } from 'assets/activity-log.svg';
import './LatestTransactions.scss';

const DocumentRequestItem = ({ request, onUploadClick, isMobile = false }) => {
  const i18n = useContext(i18nContext);

  // Format expires date
  const formatExpiresDate = (dateString) => {
    if (!dateString) return '';
    return formatDate(new Date(dateString));
  };

  // Get status badge class
  const getStatusClass = (status) => {
    switch (status) {
      case 'REQUESTED':
        return 'status-requested';
      case 'PROVIDED':
        return 'status-provided';
      case 'COMPLETED':
        return 'status-completed';
      case 'CANCELLED':
        return 'status-cancelled';
      default:
        return '';
    }
  };

  // Get localized status text
  const getStatusText = (status) => {
    return i18n.getMessage(`documentRequests.status.${status.toLowerCase()}`) || status;
  };

  if (isMobile) {
    return (
      <div className='transaction-item-mobile'>
        <div className='item-mobile-top'>
          <div className='item-mobile-date'>
            <CalendarIcon className='icon' />
            <p>{formatExpiresDate(request.expiresAt)}</p>
          </div>
          <div className='item-mobile-status'>
            <span className={`transaction-status ${getStatusClass(request.status)}`}>
              {getStatusText(request.status)}
            </span>
          </div>
        </div>

        {/* Transaction ID row */}
        <div className='item-mobile-from-to'>
          <TransactionsIcon className='icon' />
          <div>
            <p>{request.transactionNumber || request.transactionId || '-'}</p>
          </div>
        </div>

        {/* Counterparty row */}
        <div className='item-mobile-from-to'>
          <ProfileIcon className='icon' />
          <div>
            <p>{request.partyName || request.counterparty || '-'}</p>
          </div>
        </div>

        {/* Reference row - only show if exists */}
        {(request.comment || request.reference) && (
          <div className='item-mobile-from-to'>
            <ActivityIcon className='icon' />
            <div>
              <p>{request.comment || request.reference}</p>
            </div>
          </div>
        )}

        {/* Amount row with upload button */}
        <div className='item-mobile-amount'>
          <WalletIcon className='icon' />
          <p className='item-amount-text'>{request.amountDetails?.displayAmount || request.amount || '-'}</p>
          <div className='exclamation-wrapper'>
            <button
              className='upload-button-mobile-icon'
              onClick={onUploadClick}
              title={i18n.getMessage('documentRequests.uploadDocuments')}
            >
              <UploadIcon />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <tr className='transaction-item'>
      <td className='date-column'>
        <span>{request.transactionNumber || '-'}</span>
      </td>
      <td className='date-column'>
        <span>{formatExpiresDate(request.expiresAt)}</span>
      </td>
      <td className='counterparty-column'>
        <span>{request.partyName || '-'}</span>
      </td>
      <td className='reference-column'>
        <span>{request.comment || '-'}</span>
      </td>
      <td className='amount-column'>
        <span>{request.amountDetails?.displayAmount || '-'}</span>
      </td>
      <td className='status-column'>
        <span className={`transaction-status ${getStatusClass(request.status)}`}>{getStatusText(request.status)}</span>
      </td>
      <td className='upload-column'>
        <button
          className='upload-button'
          onClick={(e) => onUploadClick(e)}
          title={i18n.getMessage('documentRequests.uploadDocuments')}
        >
          <UploadIcon />
        </button>
      </td>
    </tr>
  );
};

DocumentRequestItem.propTypes = {
  request: PropTypes.object.isRequired,
  onUploadClick: PropTypes.func.isRequired,
  isMobile: PropTypes.bool
};

export default DocumentRequestItem;
